/*
 * rcc-plp-enhancements.js — PLP interaction/navigation enhancers, split out
 * of the parser-blocking head bundle (M3, 2026-08-03, CUSTOM-CODE-REVIEW A2 /
 * NEXT-ROUND M3): modules 5-7 of rcc-card-components.js verbatim. They define
 * NO custom elements and are not read by the RCC app block's data-requirements
 * bootstrap, so unlike modules 1-4 (which must stay registered before the app
 * block parses) they do not need to block parsing. Everything here runs off
 * events that fire long after defer executes: module 5 off grid mutations +
 * resize, module 6 off pagehide / back_forward enforcement (app scroll hijack
 * lands ~1.2s post-init), module 7 off the app's card-click pushState.
 * Loaded with defer from snippets/nimstrata.liquid, immediately after the
 * blocking pair — grid paint order unchanged (the QAT race-test condition).
 */

/* shared i18n accessor, replicated from rcc-card-components.js so this file
   stays standalone (same body; the blocking file still executes first) */
function rccCardStrings() {
  return (typeof window !== 'undefined' && window.rccStrings) || {};
}

/* ================================================================== */
/* 5 · paex size-pill overflow "+N" chip (design D-17, 2026-07-31)     */
/* ================================================================== */
/*
 * The card's size row never wraps (D-17): visible count is computed from
 * REAL label widths, then a "+N" text chip absorbs the rest. Previously
 * the row was clip-masked (reported as an app gap — no CSS "+N"); now
 * that the theme owns the quick-add modal the chip is buildable: clicking
 * it opens OUR modal (window.paexQuickAdd.open, paex-quick-add.js), where
 * every size is visible with its own stock (F-01) — desktop AND mobile.
 *
 * Mechanics: measure after the app renders pills (MutationObserver scoped
 * to the RCC grid's app-block wrapper — covers hydration AND load-more
 * re-renders, INCLUDING an app swap of .rcc-search itself, while drawer/
 * header/badge churn elsewhere in the body no longer wakes us; body is
 * only watched until the grid first mounts; our own chip writes are
 * filtered out), and again on debounced resize. Pills beyond the fit are
 * hidden with .paex-rcc-pill-hidden; the chip is a <button> built via
 * createElement/textContent, styled from rcc-custom.css (design moreStyle:
 * text chip, no border). a11y label = paex.rcc.show_all_sizes.
 * Reflow discipline: the fit pass runs in lockstep phases across ALL rows
 * (reset writes → one measured read pass → chip writes → per-round
 * read-then-write) so layout passes scale with the deepest row's hidden
 * count, not the page's total hidden-pill count. Per-row decisions and
 * their order are byte-for-byte those of the old sequential loop.
 */

(() => {
  const CHIP_CLASS = 'paex-rcc-more-sizes';
  const HIDDEN_CLASS = 'paex-rcc-pill-hidden';
  const ROW_SELECTOR = '.rcc-search__product__option--text';
  const PILL_SELECTOR = '.rcc-search__product__option__value--text';

  const chipLabel = () => {
    const strings = rccCardStrings();
    return (
      strings.showAllSizes ||
      (document.documentElement.lang.toLowerCase().startsWith('fr') ? 'Voir toutes les tailles' : 'Show all sizes')
    );
  };

  const buildChip = () => {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = CHIP_CLASS;
    chip.setAttribute('aria-label', chipLabel());
    chip.setAttribute('aria-haspopup', 'dialog');
    chip.textContent = '+1';
    chip.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!(window.paexQuickAdd && window.paexQuickAdd.open(chip))) {
        // Modal unavailable → fall back to the card's own product link.
        const card = chip.closest('.rcc-search__product');
        const anchor = card && card.querySelector('a[href*="/products/"]');
        if (anchor) window.location.href = anchor.href;
      }
    });
    return chip;
  };

  const adjustAll = () => {
    const rows = Array.from(document.querySelectorAll(ROW_SELECTOR));
    if (!rows.length) return;

    // Phase 1 (writes): reset our previous pass so measurement starts from truth.
    const entries = rows.map((row) => {
      row.querySelectorAll(`.${CHIP_CLASS}`).forEach((chip) => chip.remove());
      const pills = Array.from(row.querySelectorAll(PILL_SELECTOR));
      pills.forEach((pill) => pill.classList.remove(HIDDEN_CLASS));
      return { row, pills, chip: null, hidden: 0 };
    });

    // Phase 2 (reads, one layout pass): keep only rows that actually overflow.
    let active = entries.filter(
      ({ row, pills }) => pills.length >= 2 && row.scrollWidth > row.clientWidth + 1
    );
    if (!active.length) return;

    // Phase 3 (writes): give every overflowing row its chip.
    active.forEach((entry) => {
      entry.chip = buildChip();
      entry.row.append(entry.chip);
    });

    // Phase 4: hide trailing pills until each row (pills + chip) fits. The
    // chip's width changes with its count, so the fit is re-read each round —
    // but in lockstep across all rows, so each round costs ONE layout pass
    // instead of one per row. Per-row this replays the exact sequential
    // check-then-hide order (i = pills.length-1 … 1) the old loop used.
    while (active.length) {
      const still = [];
      for (const entry of active) {
        const i = entry.pills.length - 1 - entry.hidden;
        if (i > 0 && entry.row.scrollWidth > entry.row.clientWidth + 1) {
          still.push(entry);
        } else if (!entry.hidden) {
          entry.chip.remove(); // masked overflow was sub-pixel; nothing actually hidden
        }
      }
      for (const entry of still) {
        entry.pills[entry.pills.length - 1 - entry.hidden].classList.add(HIDDEN_CLASS);
        entry.hidden += 1;
        entry.chip.textContent = `+${entry.hidden}`;
      }
      active = still;
    }
  };

  let debounceTimer = null;
  const scheduleAdjust = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(adjustAll, 120);
  };

  const isOurNode = (node) =>
    node && node.nodeType === 1 && (node.classList.contains(CHIP_CLASS) || node.matches?.(`.${CHIP_CLASS}`));

  // Observe the RCC grid, not the whole body: the app-block wrapper around
  // .rcc-search is server-rendered and stable, so scoping there survives even
  // an app re-render that swaps the .rcc-search node itself. Body is watched
  // only while the grid has not mounted yet (never on pages without it).
  const observerScope = () => {
    // Theme editor re-injects whole sections on edits — keep the legacy
    // body-wide watch there so the chip pass survives section re-renders.
    if (window.Shopify && window.Shopify.designMode) return null;
    const root = document.querySelector('.rcc-search');
    return root ? root.parentElement || root : null;
  };

  const start = () => {
    let scoped = false;
    const observer = new MutationObserver((mutations) => {
      if (!scoped) {
        const scope = observerScope();
        if (scope) {
          scoped = true;
          observer.disconnect();
          observer.observe(scope, { childList: true, subtree: true });
          scheduleAdjust();
          return;
        }
      }
      for (const mutation of mutations) {
        const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
        if (nodes.some((node) => node.nodeType === 1 && !isOurNode(node))) {
          scheduleAdjust();
          return;
        }
      }
    });
    const scope = observerScope();
    if (scope) scoped = true;
    observer.observe(scope || document.body, { childList: true, subtree: true });
    window.addEventListener('resize', scheduleAdjust);
    scheduleAdjust();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* ================================================================== */
/* 6 · PLP back-navigation scroll restore (2026-07-31)                 */
/* ================================================================== */
/*
 * Merchant bug: PLP → PDP → back lands at the top of the grid. Measured
 * root cause (QAT, bfcache disabled): the browser's own restore lands
 * correctly (~2372px), then the RCC app calls
 * window.scrollTo({top:~130, behavior:'smooth'}) from search-react.js
 * ~1.2s after load, stomping the position. bfcache resumes are FINE
 * (persisted=true keeps the position and the app does not re-init), so
 * this module acts ONLY on real back_forward document loads.
 *
 * Mechanism: on pagehide the position is memoized per FULL href (query
 * params included — filters/load-more return to the same href). On a
 * back_forward load with a matching memo, an enforcement loop (from
 * retail-connect:search-grid:ready, hard fallback 1.2s, for 3s) re-pins
 * scrollY whenever something moves it — covering both the app's smooth
 * scroll hijack and late height growth from card hydration. Real user
 * input (wheel/touch/key/pointer) aborts immediately — we never fight
 * the user. history.scrollRestoration stays 'auto': the browser's
 * restore is a good first approximation and bfcache stays untouched.
 */

(() => {
  const MEMO_KEY = 'paex-plp-scroll';
  const MEMO_MAX_AGE = 30 * 60 * 1000;
  const MIN_Y = 300; // near-top exits are not worth restoring
  const TOLERANCE = 80;
  // 2026-08-01 (regression runner, both engines): 3s was not enough — under
  // slow hydration the app's ~1.2s-after-init scrollTo({top:~130,smooth})
  // landed AFTER our window closed and won (measured restored=85/362 vs
  // saved=1800). The window is now 6s AND, more importantly, the hijack is
  // swallowed at the source below (window.scrollTo wrap), so the deadline
  // is only a cleanup bound, not a correctness one.
  const ENFORCE_MS = 6000;
  const FALLBACK_START_MS = 800;

  // URL identity ignores the app's `pid` param (2026-08-01, measured live:
  // the RCC card link navigates via THIS COLLECTION's URL + ?pid=<product>,
  // and BACK lands on that pid-URL — an exact-href match therefore NEVER
  // engaged on the main PLP→PDP→back path, which is why the restore kept
  // "regressing". Every other param (filters, page, q) still distinguishes.)
  const memoKey = (href) => {
    try {
      const url = new URL(href);
      url.searchParams.delete('pid');
      // the same click also stamps page=1 (journal-verified); a bare URL and
      // ?page=1 are the same grid, so normalize — real pagination (page>1)
      // still distinguishes
      if (url.searchParams.get('page') === '1') url.searchParams.delete('page');
      return url.href;
    } catch (error) {
      return href;
    }
  };

  window.addEventListener('pagehide', () => {
    try {
      if (window.scrollY > MIN_Y) {
        sessionStorage.setItem(
          MEMO_KEY,
          JSON.stringify({ url: memoKey(location.href), y: Math.round(window.scrollY), t: Date.now() })
        );
      } else {
        sessionStorage.removeItem(MEMO_KEY);
      }
    } catch (error) {
      /* sessionStorage unavailable — feature degrades to browser default */
    }
  });

  const entry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (!entry || entry.type !== 'back_forward') return;

  let memo = null;
  try {
    memo = JSON.parse(sessionStorage.getItem(MEMO_KEY) || 'null');
  } catch (error) {
    memo = null;
  }
  if (!memo || memo.url !== memoKey(location.href) || Date.now() - memo.t > MEMO_MAX_AGE || !(memo.y > MIN_Y)) return;

  let userMoved = false;

  // HIJACK GUARD (2026-08-01): the enforcement loop alone LOST whenever the
  // app's smooth scrollTo fired after the loop's window — root cause of the
  // merchant's recurring "back lands near the top". While the memo is
  // active, programmatic window.scrollTo calls that target anywhere OTHER
  // than the memoized position are swallowed (the app's grid-top hijack is
  // exactly such a call); our own re-pin passes through because it targets
  // memo.y. Any real user input tears the wrap down immediately — after
  // that the page behaves stock.
  const nativeScrollTo = window.scrollTo.bind(window);
  let guardActive = true;
  const releaseGuard = () => {
    guardActive = false;
    window.scrollTo = nativeScrollTo;
  };
  window.scrollTo = function (a, b) {
    if (guardActive) {
      const top = typeof a === 'object' && a !== null ? a.top : b;
      if (typeof top === 'number' && Math.abs(top - memo.y) > TOLERANCE) return undefined;
    }
    return typeof a === 'object' && a !== null ? nativeScrollTo(a) : nativeScrollTo(a, b);
  };

  const inputEvents = ['wheel', 'touchmove', 'keydown', 'pointerdown'];
  const onInput = () => {
    userMoved = true;
    releaseGuard();
    inputEvents.forEach((name) => window.removeEventListener(name, onInput, true));
  };
  inputEvents.forEach((name) => window.addEventListener(name, onInput, { capture: true, passive: true }));

  let deadline = 0;
  const tick = () => {
    if (userMoved || Date.now() > deadline) {
      releaseGuard();
      return;
    }
    if (Math.abs(window.scrollY - memo.y) > TOLERANCE) {
      nativeScrollTo({ top: memo.y, left: 0, behavior: 'instant' });
    }
    requestAnimationFrame(tick);
  };
  const start = () => {
    if (deadline || userMoved) return;
    deadline = Date.now() + ENFORCE_MS;
    requestAnimationFrame(tick);
  };
  window.addEventListener('retail-connect:search-grid:ready', start, { once: true });
  setTimeout(start, FALLBACK_START_MS);
})();

/* ================================================================== */
/* 7 · PLP back-button de-trap: same-grid pushState → replaceState     */
/*     (launch audit item 15, 2026-08-01)                              */
/* ================================================================== */
/*
 * Merchant bug: PLP → PDP → Back → Back leaves the shopper stuck on the
 * PLP. Measured root cause (module 6's journal): the RCC app's card click
 * first pushState-es THIS COLLECTION's URL + ?pid=<product> (and page=1)
 * and only then navigates to the PDP — so the session holds TWO history
 * entries for the same grid and the second Back merely walks pid-URL →
 * bare-URL instead of exiting.
 *
 * Fix at the source: while a PLP surface is loaded (this file only ships
 * on collection/search templates via nimstrata.liquid), a pushState whose
 * URL is the SAME GRID as the current entry — identical after dropping
 * `pid` and a redundant `page=1`, the exact normalization module 6 uses —
 * becomes replaceState. Real state changes (filters, q, page>1) differ
 * after normalization and keep their pushState, so facet/filter Back
 * behavior is untouched. history.state consumers see no difference:
 * the entry's state/url are identical either way, there is just no
 * duplicate entry behind it.
 */

(() => {
  const sameGrid = (a, b) => {
    try {
      const norm = (href) => {
        const url = new URL(href, location.href);
        url.searchParams.delete('pid');
        if (url.searchParams.get('page') === '1') url.searchParams.delete('page');
        return url.href;
      };
      return norm(a) === norm(b);
    } catch (error) {
      return false;
    }
  };
  const nativePushState = history.pushState.bind(history);
  history.pushState = function (state, title, url) {
    if (url != null && url !== '' && sameGrid(String(url), location.href)) {
      return history.replaceState(state, title, url);
    }
    return nativePushState(state, title, url);
  };
})();
